// One stock count (plan §9.5; D-049; B-035): per row, what the system held at the count's
// time against what was counted, the difference and its value, whether the row was counted,
// not found (in scope with stock but no line: counted as 0) or a new lot, and what needed
// approval. Rows a later count reached first are shown as superseded: kept, not adjusted.
// "Above the limit" marks only the rows that moved stock, with the product's net that the
// approval judged (D-049, UI4A-205); a rejection says what it actually reversed (UI4A-204),
// and the rows it reversed are marked like superseded ones, out of the figures (B-087).

import type { Approval, CountDetail } from '@enp/domain/api';
import { addQty, qtyToString } from '@enp/domain/quantity';
import { formatCount } from '@enp/domain/format';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  LoadingSkeleton,
  Money,
  PageHeader,
  Qty,
  StatTile,
  StatusBadge,
  type ColumnDef,
} from '@enp/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, ClipboardList, EyeOff, Flag, Layers, Undo2 } from 'lucide-react';
import { meQueryOptions } from '../../../../auth/session.js';
import { ApiError } from '../../../../lib/api.js';
import { AccountDate, useAccountTimeZone } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { plural, SignedMoney } from '../../../../stock-control/parts.js';
import { approvalQuery, countQuery } from '../../../../stock-control/queries.js';
import {
  COUNT_SCOPE_LABEL,
  countsNamed,
  countStatus,
  LINE_KIND,
  signedText,
} from '../../../../stock-control/words.js';
import {
  BestBefore,
  LEDGER_DECIMALS,
  SignedQty,
  StockStateBadge,
} from '../../../../stock/components.js';
import { calendarToday } from '../../../../stock/labels.js';

export const Route = createFileRoute('/_app/stock/counts/$countId')({
  component: CountPage,
});

type Line = CountDetail['lines'][number];

const TABLE_LABEL = 'Counted rows';

function Trail({ page }: { page: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/stock">Stock</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/stock/counts">Counts</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{page}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Each product's net change over its rows that moved stock (superseded rows left out):
 * what the approval judged it by (D-049).
 */
function productNets(lines: readonly Line[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const l of lines) {
    if (l.superseded_by) continue;
    out.set(l.product.id, qtyToString(addQty(out.get(l.product.id) ?? '0', l.variance)));
  }
  return out;
}

const differs = (l: Line) => !/^[-+]?0*(\.0*)?$/.test(l.variance.trim());

function lineColumns(
  today: string,
  nets: Map<string, string>,
  approvalId: string | null,
): ColumnDef<Line, unknown>[] {
  const col = createColumnHelper<Line>();
  return [
    col.display({
      id: 'product',
      header: 'Product and lot',
      meta: { width: 'min-w-52' },
      cell: ({ row }) => {
        const l = row.original;
        return (
          <span className="flex flex-col gap-1 leading-tight">
            <span className="font-medium text-fg">{l.product.name}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              {l.lot ? (
                <span className="font-mono">{l.lot.lot_code}</span>
              ) : (
                <span>Not tracked by lot</span>
              )}
              <StockStateBadge state={l.state} />
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'best_before',
      header: 'Best-before',
      meta: { className: 'hidden xl:table-cell' },
      cell: ({ row }) => <BestBefore date={row.original.lot?.best_before ?? null} today={today} />,
    }),
    col.display({
      id: 'kind',
      header: 'Row',
      cell: ({ row }) => {
        const k = LINE_KIND[row.original.line_kind];
        return (
          <span className="flex flex-col items-start gap-0.5">
            <Badge tone={row.original.line_kind === 'counted' ? 'outline' : 'neutral'}>
              {k.label}
            </Badge>
            {row.original.line_kind !== 'counted' && (
              <span className="max-w-48 text-xs text-muted">{k.explain}</span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'system',
      header: 'System at count',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Qty
          value={row.original.system_qty}
          unit={row.original.product.base_uom}
          maxDecimals={LEDGER_DECIMALS}
          className="text-muted"
        />
      ),
    }),
    col.display({
      id: 'counted',
      header: 'Counted',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Qty
          value={row.original.counted_qty}
          unit={row.original.product.base_uom}
          maxDecimals={LEDGER_DECIMALS}
          className="font-semibold text-fg"
        />
      ),
    }),
    col.display({
      id: 'variance',
      header: 'Difference',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <SignedQty
          value={row.original.variance}
          unit={row.original.product.base_uom}
          noted={row.original.superseded_by !== null || row.original.reversed}
        />
      ),
    }),
    col.display({
      id: 'value',
      header: 'Value',
      meta: { align: 'right' },
      cell: ({ row }) =>
        row.original.value_cents === null ? (
          <span className="whitespace-nowrap text-sm text-muted">No cost</span>
        ) : row.original.superseded_by || row.original.reversed ? (
          // Noted, never booked (UI4A-1), or booked and reversed (B-087): no colour of a
          // loss or a gain.
          <span className="whitespace-nowrap">
            <SignedMoney
              cents={row.original.value_cents}
              className="font-medium text-muted line-through"
            />
            {/* The strike-through says it to the eye only (UI4A-212). */}
            <span className="sr-only">
              {row.original.reversed ? ' (reversed, not booked)' : ' (noted, not booked)'}
            </span>
          </span>
        ) : (
          <SignedMoney cents={row.original.value_cents} lossTone className="font-medium" />
        ),
    }),
    col.display({
      id: 'notes',
      header: 'Notes',
      cell: ({ row }) => {
        const l = row.original;
        if (l.superseded_by) {
          return (
            <span className="flex max-w-64 flex-col gap-0.5 text-xs text-muted">
              <Badge tone="neutral" className="w-fit">
                <Layers aria-hidden />
                Superseded by{' '}
                <Link
                  to="/stock/counts/$countId"
                  params={{ countId: l.superseded_by.id }}
                  onClick={(e) => e.stopPropagation()}
                  className="font-mono underline underline-offset-2"
                >
                  {l.superseded_by.number}
                </Link>
              </Badge>
              A later count of this row reached the office first and already reflects the shelf, so
              this difference was noted, not booked.
            </span>
          );
        }
        // Reversed on rejection (B-087): no longer above any limit, nothing stands.
        if (l.reversed) {
          return (
            <span className="flex max-w-64 flex-col gap-0.5 text-xs text-muted">
              <Badge tone="neutral" className="w-fit">
                <Undo2 aria-hidden />
                {approvalId ? (
                  <Link
                    to="/approvals"
                    search={{ view: 'rejected', id: approvalId }}
                    onClick={(e) => e.stopPropagation()}
                    className="underline underline-offset-2"
                  >
                    Reversed
                  </Link>
                ) : (
                  'Reversed'
                )}
              </Badge>
              A manager rejected this difference and a correcting movement undid it, so it is not in
              the stock figures.
            </span>
          );
        }
        // Only a row that moved stock is above the limit, and the limit is judged on the
        // product's net, so it says so (UI4A-205).
        if (l.needs_approval && differs(l)) {
          const net = nets.get(l.product.id) ?? l.variance;
          return (
            <span className="flex max-w-64 flex-col items-start gap-0.5">
              <Badge tone="warning">
                <Flag aria-hidden />
                Above the limit
              </Badge>
              <span className="text-xs text-muted">
                {l.product.name} net {signedText(net, l.product.base_uom)} is above the limit
              </span>
            </span>
          );
        }
        if (l.movement_id) return <span className="text-sm text-muted">Corrected</span>;
        return <span className="text-sm text-muted">Matched</span>;
      },
    }),
  ];
}

/** The count's approval as the approvals inbox reads it, once decided (UI4A-12, UI4A-204). */
function useDecided(approvalId: string | null): Approval | undefined {
  const q = useQuery({ ...approvalQuery(approvalId ?? ''), enabled: approvalId !== null });
  return q.data;
}

function ApprovalNotice({ count }: { count: CountDetail }) {
  const a = count.approval;
  const decided = useDecided(a && a.status !== 'requested' ? a.id : null);
  const timeZone = useAccountTimeZone();
  const decider = decided?.decided_by?.name ?? null;
  if (count.status === 'superseded') {
    return (
      <Notice tone="neutral" title="Superseded: nothing was adjusted">
        A later count of every row reached the office first. That count already reflects the shelf,
        so this count is kept for the record without changing stock.
      </Notice>
    );
  }
  if (!a) return null;
  const open = (
    <Link
      to="/approvals"
      search={{ view: a.status === 'requested' ? undefined : a.status, id: a.id }}
      className="inline-flex items-center gap-1 font-medium text-primary-text underline-offset-2 hover:underline"
    >
      Open in Approvals
      <ArrowRight aria-hidden className="size-4" />
    </Link>
  );
  if (a.status === 'requested') {
    return (
      <Notice tone="warning" title="Waiting for a manager">
        Some differences were above the approval limit and no manager’s PIN was given at the
        station. They are already in the stock figures; a manager confirms or reverses them. {open}
      </Notice>
    );
  }
  if (a.status === 'rejected') {
    // Worded from what the rejection did: the outcome and the counts it left (UI4A-204).
    const outcome = decided?.reversal?.outcome ?? a.reversal;
    const later = decided?.later_counts ?? [];
    const counts = countsNamed(later, timeZone);
    const found = later.length === 1 ? 'that count' : 'those counts';
    const who = decider ?? 'A manager';
    return (
      <Notice
        tone="danger"
        title={
          outcome === 'not_reversed'
            ? 'Rejected: nothing was reversed'
            : outcome === 'partly_reversed'
              ? 'Rejected: partly reversed'
              : 'Rejected: the differences above the limit were reversed'
        }
      >
        {outcome === 'not_reversed'
          ? `${who} rejected the differences above the limit, but every row was counted again since${counts ? `, in ${counts}` : ''}, so nothing was reversed: ${found} ${later.length === 1 ? 'stands' : 'stand'}.`
          : outcome === 'partly_reversed'
            ? `${who} rejected the differences above the limit. Correcting movements reversed the rows nobody had counted since; the rows counted again${counts ? ` in ${counts}` : ' since'} keep what ${found} found.`
            : `${who} rejected the differences above the limit; correcting movements reversed them.`}{' '}
        {open}
      </Notice>
    );
  }
  return (
    <Notice
      tone="neutral"
      title={a.mode === 'manager_pin' ? 'Approved with a manager’s PIN' : 'Approved'}
    >
      {a.mode === 'manager_pin'
        ? `${decider ?? 'A manager'} approved the differences above the limit with a PIN at the station.`
        : `${decider ?? 'A manager'} confirmed the differences above the limit.`}{' '}
      {open}
    </Notice>
  );
}

/**
 * How many rows differed, true to what was booked (UI4A-1): a superseded row's difference
 * was noted, not booked — never "every row matched".
 */
function DifferedTile({ count }: { count: CountDetail }) {
  const booked = count.variance_count;
  const noted = count.noted_variance_count;
  const reversed = count.reversed_variance_count;
  // The rows that differed but don't stand (B-087), after the ones that do.
  const apart = [
    ...(reversed > 0 ? [`${formatCount(reversed)} reversed on rejection`] : []),
    ...(noted > 0 ? [`${formatCount(noted)} noted, not booked`] : []),
  ].join('; ');
  if (booked === 0 && (noted > 0 || reversed > 0)) {
    return (
      <StatTile
        label="Differed"
        value={noted + reversed}
        hint={
          reversed === 0
            ? `${noted === 1 ? 'difference' : 'differences'} noted, not booked (superseded)`
            : noted === 0
              ? `${reversed === 1 ? 'difference' : 'differences'} reversed on rejection, not booked`
              : `none in the ledger: ${apart}`
        }
        tone="neutral"
      />
    );
  }
  return (
    <StatTile
      label="Differed"
      value={booked}
      hint={
        booked === 0
          ? 'Every row matched'
          : apart
            ? `rows corrected in the ledger; ${apart}`
            : 'rows corrected in the ledger'
      }
      tone={booked > 0 ? 'warning' : 'success'}
    />
  );
}

/** Net value as it stands, and what the count found before a rejection reversed part (B-087). */
function NetTile({ count }: { count: CountDetail }) {
  const net = count.net_value_cents;
  const reversed = count.reversed_net_value_cents;
  return (
    <StatTile
      label="Net value"
      value={<SignedMoney cents={net} lossTone />}
      hint={
        count.reversed_variance_count > 0 ? (
          <>
            what stands; counted <SignedMoney cents={net + reversed} />,{' '}
            <SignedMoney cents={reversed} /> reversed on rejection
          </>
        ) : (
          'gains less losses, at standard cost'
        )
      }
      tone={net < 0 ? 'danger' : 'neutral'}
    />
  );
}

function CountPage() {
  const { countId } = Route.useParams();
  const q = useQuery(countQuery(countId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const navigate = useNavigate();
  // Best-before judgements (days left, near expiry) use the calendar date, not the trading day.
  const today = calendarToday(me.account.timezone);
  const count = q.data;
  useDocumentTitle(count ? `Count ${count.number}` : 'Stock count');

  const back = (
    <Button variant="secondary" asChild>
      <Link to="/stock/counts">
        <ArrowLeft aria-hidden />
        All counts
      </Link>
    </Button>
  );

  if (q.isError) {
    const missing =
      q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 400);
    return missing ? (
      <>
        <div className="pb-4">
          <Trail page="Not found" />
        </div>
        <EmptyState
          icon={ClipboardList}
          titleAs="h1"
          title="Count not found"
          description="There’s no stock count at this address. It may belong to a branch you can’t see, or the link is mistyped."
          action={back}
        />
      </>
    ) : (
      <div className="max-w-2xl">
        <ErrorPanel
          error={q.error}
          what="This count"
          onRetry={() => void q.refetch()}
          extraAction={back}
        />
      </div>
    );
  }
  if (!count) {
    return (
      <>
        <h1 className="sr-only">Loading the count</h1>
        <LoadingSkeleton variant="page" label="Loading the count" />
      </>
    );
  }

  const status = countStatus(count);
  const where = count.location
    ? `${count.location.name} · ${count.branch.name}`
    : count.branch.name;

  return (
    <>
      <PageHeader
        eyebrow={<Trail page={count.number} />}
        title={`Count ${count.number}`}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              {where} · {COUNT_SCOPE_LABEL[count.scope].toLowerCase()} · counted{' '}
              <AccountDate value={count.counted_at} withTime /> by{' '}
              {count.counted_by?.name ?? 'someone this account doesn’t know'}
            </span>
          </span>
        }
        actions={
          <>
            <StatusBadge status={status.status}>{status.label}</StatusBadge>
            {count.blind && (
              <Badge tone="neutral">
                <EyeOff aria-hidden />
                Blind count
              </Badge>
            )}
          </>
        }
      />
      <div className="flex flex-col gap-5">
        <ApprovalNotice count={count} />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile label="Rows" value={count.line_count} hint="counted, not found or new" />
          <DifferedTile count={count} />
          <NetTile count={count} />
          <StatTile
            label="Moved"
            value={<Money cents={count.gross_value_cents} />}
            hint={
              count.uncosted_variance_count > 0
                ? `plus ${plural(count.uncosted_variance_count, 'row', 'rows')} without a cost`
                : count.reversed_variance_count > 0
                  ? 'every difference that stands, up or down'
                  : 'every difference, up or down'
            }
          />
        </div>
        {count.blind && (
          <p className="text-sm text-muted">
            Blind count: the person counting didn’t see the system’s figures. You see them here.
          </p>
        )}
        {count.note && (
          <blockquote className="border-l-2 border-border-strong pl-3 text-body text-fg">
            “{count.note}”
          </blockquote>
        )}
        <DataTable
          label={TABLE_LABEL}
          columns={lineColumns(today, productNets(count.lines), count.approval?.id ?? null)}
          data={count.lines}
          filterable={false}
          getRowId={(l, i) => `${l.product.id}:${l.lot?.id ?? 'none'}:${l.state}:${i}`}
          onRowActivate={(l) =>
            void navigate({
              to: '/stock/movements',
              search: l.lot ? { product: l.product.id, lot: l.lot.id } : { product: l.product.id },
            })
          }
          empty={
            <EmptyState
              compact
              icon={ClipboardList}
              title="No rows in this count"
              description="Nothing was counted and nothing in its scope had stock at the time."
            />
          }
          footer={
            count.lines.length > 0 ? (
              <p className="text-sm text-muted">
                System figures are the ledger at the count’s time; sales after it are never
                corrected twice. Open a row to see its movements.
              </p>
            ) : undefined
          }
        />
        <div>{back}</div>
      </div>
    </>
  );
}
